/**
 * 매핑 JSON 탭의 텍스트 ↔ 매핑.
 *
 * 텍스트는 wire 그대로다(snake_case · 모든 키). 키는 스키마 순서로 쓰고 들여쓰기는 2칸이다. 편집본을
 * 그대로 쓴다. 저장 · 검증 · POC 도 편집본을 그대로 보낸다. 그래서 이 텍스트가 곧 보내는 본문이다.
 *
 * 읽기는 두 단계다. 먼저 문법을 본다. 문법이 맞으면 읽기 스키마(`checkMapping`)로 모양을 본다. 둘 다 통과할
 * 때만 매핑이 된다. 기본값을 채우지 않는다. 사람이 친 JSON 도 BE 응답처럼 모든 키가 있어야 한다.
 *
 * Owns:
 * - 텍스트 형식, 문제의 한국어 문장과 줄 · 열 범위.
 *
 * Does not own:
 * - 적용할지와 적용 안 한 텍스트 — `mappingEditor.ts`. 그리기 — `page/`.
 */
import { lineColumnAt, parseJsonDocument, type JsonLocation, type JsonSpan } from "@/shared/json";
import { checkMapping, orderMappingKeys } from "../contract/mappingSchema";
import type { Mapping } from "../domain/mapping";

/** JSON 텍스트의 문제 하나. 줄 · 열은 1부터 센다. 끝 열은 범위에 들지 않는다(Monaco 규약). */
export type JsonProblem = {
  readonly startLine: number;
  readonly startColumn: number;
  readonly endLine: number;
  readonly endColumn: number;
  /** JSON 점 경로(예: `hands.0.side`). 문법 오류는 `null`, 값 전체의 문제는 빈 문자열이다. */
  readonly path: string | null;
  readonly text: string;
};

export type MappingJsonRead =
  | { readonly ok: true; readonly mapping: Mapping }
  | { readonly ok: false; readonly problems: readonly JsonProblem[] };

export function formatMappingJson(mapping: Mapping): string {
  return `${JSON.stringify(orderMappingKeys(mapping), null, 2)}\n`;
}

export function readMappingJson(text: string): MappingJsonRead {
  const parsed = parseJsonDocument(text);
  if (!parsed.ok) {
    return {
      ok: false,
      problems: [toProblem(text, parsed.problem.span, null, parsed.problem.text)],
    };
  }
  const checked = checkMapping(parsed.document.value);
  if (checked.ok) {
    return { ok: true, mapping: checked.value };
  }
  const problems = checked.problems.map((problem) =>
    toProblem(text, problemSpan(parsed.document.locate(problem.path)), problem.path, problem.text),
  );
  return { ok: false, problems: problems.sort(byPosition) };
}

/**
 * 문제를 칠할 범위. property 는 키부터 칠한다. 원시값이면 값 끝까지, 객체 · 배열이면 키만 칠한다. 여러 줄을
 * 칠하지 않기 위해서다. 키가 없는 값(배열 항목 · 전체)은 원시값이면 값을, 아니면 여는 괄호를 칠한다.
 * 빠진 키의 문제는 `locate` 가 부모를 준다. 그래서 부모의 키에 칠해진다.
 */
function problemSpan(location: JsonLocation): JsonSpan {
  const { container, key, value } = location;
  if (key !== null) {
    return container
      ? key
      : { offset: key.offset, length: value.offset + value.length - key.offset };
  }
  return container ? { offset: value.offset, length: 1 } : value;
}

function toProblem(
  text: string,
  span: JsonSpan,
  path: string | null,
  message: string,
): JsonProblem {
  const start = lineColumnAt(text, span.offset);
  const end = lineColumnAt(text, span.offset + span.length);
  // 빈 범위(예: 빈 텍스트)도 보이게 한 칸을 칠한다.
  const empty = end.line === start.line && end.column <= start.column;
  return {
    startLine: start.line,
    startColumn: start.column,
    endLine: end.line,
    endColumn: empty ? start.column + 1 : end.column,
    path,
    text: message,
  };
}

function byPosition(left: JsonProblem, right: JsonProblem): number {
  return left.startLine - right.startLine || left.startColumn - right.startColumn;
}
